import { SimpleGrid, Text, ThemeIcon, UnstyledButton, Stack, Group, Box } from '@mantine/core';
import type { ProviderType } from '@bmai/shared';
import { providerIconForType } from '@/shared/ProviderIconComponent';

interface IntegrationProviderTypeGridProps {
    providerTypes: ProviderType[];
    selectedId: string | null;
    onSelect: (providerTypeId: string) => void;
}

function providerInitials(displayName: string): string {
    const parts = displayName.split(/\s+/).filter(Boolean);
    const letters = parts
        .slice(0, 2)
        .map((p) => p.charAt(0).toUpperCase())
        .join('');
    return letters || '?';
}

export default function IntegrationProviderTypeGrid({ providerTypes, selectedId, onSelect }: IntegrationProviderTypeGridProps): React.ReactElement {
    if (providerTypes.length === 0) {
        return (
            <Box
                p="xl"
                style={{
                    border: '1px dashed var(--bm-color-border)',
                    borderRadius: 'var(--bm-radius)',
                    textAlign: 'center',
                }}
            >
                <Text size="sm" c="dimmed">
                    No provider types available
                </Text>
            </Box>
        );
    }

    return (
        <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="sm">
            {providerTypes.map((type) => {
                const isSelected = selectedId === type.id;
                const icon = providerIconForType(type.id, 48);
                return (
                    <UnstyledButton
                        key={type.id}
                        onClick={() => onSelect(type.id)}
                        p="sm"
                        style={{
                            border: `1px solid ${isSelected ? 'var(--bm-color-brand-accent)' : 'var(--bm-color-border)'}`,
                            borderRadius: 'var(--bm-radius)',
                            background: isSelected ? 'var(--bm-color-brand-soft)' : 'transparent',
                            transition: 'border-color 150ms, background 150ms',
                        }}
                    >
                        <Group gap="sm" wrap="nowrap" align="center">
                            <ThemeIcon size={48} radius="md" variant={icon ? 'transparent' : isSelected ? 'filled' : 'light'} color="indigo">
                                {icon ?? providerInitials(type.displayName)}
                            </ThemeIcon>
                            <Stack gap={2}>
                                <Text fw={600} size="md">
                                    {type.displayName}
                                </Text>
                            </Stack>
                        </Group>
                    </UnstyledButton>
                );
            })}
        </SimpleGrid>
    );
}
